import { useQuery, type UseQueryOptions } from '@tanstack/react-query';

import apiClient from '../../lib/api-client';

// ============================================================================
// Types
// ============================================================================

export interface ForwarderBreakdown {
  forwardedBy: string;
  count: number;
  totalAccountValue: number;
}

export interface ForwardsSummary {
  totalForwards: number;
  totalAccountValueForwarded: number;
  forwardsThisWeek: number;
  forwardsThisMonth: number;
  byForwarder: ForwarderBreakdown[];
}

export interface TopRecommendedProduct {
  carrierName: string;
  productName: string;
  productType: string | null;
  count: number;
  avgRecommendationScore: number;
}

export interface TopReplacedCarrier {
  carrierName: string;
  count: number;
  totalAccountValue: number;
}

export interface EditPatterns {
  summaryEdits: {
    edited: number;
    keptOriginal: number;
    noOriginal: number;
  };
  productReorders: {
    reordered: number;
    keptOriginal: number;
    noOriginal: number;
  };
  topReorderPatterns: Array<{
    originalPosition: number;
    newPosition: number;
    count: number;
  }>;
}

export interface ForwardsAnalytics {
  summary: ForwardsSummary;
  topRecommendedProducts: TopRecommendedProduct[];
  topReplacedCarriers: TopReplacedCarrier[];
  editPatterns: EditPatterns;
}

// ============================================================================
// Query Keys
// ============================================================================

export const forwardsAnalyticsKeys = {
  all: ['forwards-analytics'] as const,
  data: () => [...forwardsAnalyticsKeys.all, 'data'] as const,
};

// ============================================================================
// API
// ============================================================================

async function fetchForwardsAnalytics(): Promise<ForwardsAnalytics> {
  const response = await apiClient.get<ForwardsAnalytics>('/opportunities/analytics/forwards');
  return response.data;
}

// ============================================================================
// Hook
// ============================================================================

export function useForwardsAnalytics(
  options?: Omit<UseQueryOptions<ForwardsAnalytics>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: forwardsAnalyticsKeys.data(),
    queryFn: fetchForwardsAnalytics,
    staleTime: 2 * 60 * 1000,
    ...options,
  });
}
